// Admin Invoice utility functions
export const getAdminInvoice = (booking, payments) => {
  // Helper function to safely parse numeric values (handles strings from database)
  const getNumeric = (val) => {
    if (val === null || val === undefined || val === '') return 0;
    const strVal = String(val).trim();
    if (strVal === '' || strVal === 'null' || strVal === 'undefined' || strVal === 'NaN') {
      return 0;
    }
    const cleaned = strVal.replace(/[^0-9.-]/g, '');
    if (cleaned === '' || cleaned === '-' || cleaned === '.') {
      return 0;
    }
    const parsed = parseFloat(cleaned);
    if (isNaN(parsed) || !isFinite(parsed)) {
      return 0;
    }
    return parsed;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateForInvoice = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrencyForInvoice = (value) => {
    const num = getNumeric(value);
    if (isNaN(num) || !isFinite(num)) {
      return '0.00';
    }
    return num.toFixed(2);
  };

  const generateInvoiceHTML = () => {
    const completedPayments = payments.filter(p => p.status === 'completed');
    const totalPaid = completedPayments.reduce((sum, p) => {
      return sum + getNumeric(p.amount || 0);
    }, 0);
    
    const client = booking?.client;
    const vendor = booking?.vendor;
    const service = booking?.vendor_service || booking?.vendorService;
    const packageData = booking?.vendor_service_package || booking?.vendorServicePackage;
    const addons = booking?.booking_addons || booking?.bookingAddons || [];
    const addonsTotal = addons.reduce((sum, addon) => sum + getNumeric(addon.addon_price || 0), 0);
    
    // Calculate amounts using new payment structure
    const subtotal = getNumeric(booking?.subtotal_amount || 0);
    const platformFee = getNumeric(booking?.platform_fee || 0);
    const totalAmountDue = subtotal + platformFee; // What client pays
    const vendorPayout = getNumeric(booking?.vendor_payout_amount || booking?.subtotal_amount || 0);
    
    // Calculate deposit amount from actual payments
    const depositAmountPaid = completedPayments
      .filter(p => p.payment_type === 'deposit')
      .reduce((sum, p) => sum + getNumeric(p.amount || 0), 0);
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice - ${booking?.booking_reference || 'N/A'}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #000; padding-bottom: 20px; }
          .invoice-title { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
          .booking-ref { font-size: 14px; color: #666; }
          .section { margin-bottom: 25px; }
          .section-title { font-size: 18px; font-weight: bold; margin-bottom: 15px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
          .row { display: flex; justify-content: space-between; margin-bottom: 8px; }
          .label { font-weight: 600; }
          .value { text-align: right; }
          .payment-item { margin-bottom: 10px; padding: 10px; background: #f5f5f5; border-radius: 4px; }
          .total { font-size: 18px; font-weight: bold; margin-top: 20px; padding-top: 15px; border-top: 2px solid #000; }
          .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #666; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="invoice-title">INVOICE</div>
          <div class="booking-ref">Booking #${booking?.booking_reference || 'N/A'}</div>
          <div style="margin-top: 10px; font-size: 12px;">Date: ${formatDate(booking?.created_at)}</div>
        </div>

        <div class="section">
          <div class="section-title">Client Information</div>
          <div class="row"><span class="label">Name:</span><span class="value">${client?.name || 'N/A'}</span></div>
          <div class="row"><span class="label">Email:</span><span class="value">${client?.email || 'N/A'}</span></div>
          <div class="row"><span class="label">Phone:</span><span class="value">${client?.phone || 'N/A'}</span></div>
        </div>

        <div class="section">
          <div class="section-title">Vendor Information</div>
          <div class="row"><span class="label">Business Name:</span><span class="value">${vendor?.business_name || 'N/A'}</span></div>
          <div class="row"><span class="label">Email:</span><span class="value">${vendor?.user?.email || 'N/A'}</span></div>
          <div class="row"><span class="label">Phone:</span><span class="value">${vendor?.business_phone || 'N/A'}</span></div>
        </div>

        <div class="section">
          <div class="section-title">Booking Details</div>
          <div class="row"><span class="label">Event Date:</span><span class="value">${formatDate(booking?.event_date)}</span></div>
          <div class="row"><span class="label">Service:</span><span class="value">${service?.service_name || 'N/A'}</span></div>
          ${packageData ? `<div class="row"><span class="label">Package:</span><span class="value">${packageData.package_name || 'N/A'}</span></div>` : ''}
          ${booking?.event_location ? `<div class="row"><span class="label">Location:</span><span class="value">${booking.event_location}</span></div>` : ''}
        </div>

        <div class="section">
          <div class="section-title">Payment Summary</div>
          <div class="row"><span class="label">Subtotal:</span><span class="value">$${formatCurrencyForInvoice(subtotal)}</span></div>
          ${addonsTotal > 0 ? `<div class="row"><span class="label">Add-ons:</span><span class="value">$${formatCurrencyForInvoice(addonsTotal)}</span></div>` : ''}
          <div class="row"><span class="label">Service Charge</span><span class="value">$${formatCurrencyForInvoice(platformFee)}</span></div>
          <div class="row"><span class="label">Total Amount Due:</span><span class="value">$${formatCurrencyForInvoice(totalAmountDue)}</span></div>
          ${depositAmountPaid > 0 ? `<div class="row"><span class="label">Deposit Amount:</span><span class="value">$${formatCurrencyForInvoice(depositAmountPaid)}</span></div>` : ''}
          <div class="row"><span class="label">Vendor Payout:</span><span class="value">$${formatCurrencyForInvoice(vendorPayout)}</span></div>
        </div>

        <div class="section">
          <div class="section-title">Payment Transactions</div>
          ${completedPayments.length > 0 ? completedPayments.map(payment => {
            const paymentAmount = getNumeric(payment.amount || 0);
            return `
            <div class="payment-item">
              <div class="row"><span class="label">Payment Reference:</span><span class="value">${payment.payment_reference || 'N/A'}</span></div>
              <div class="row"><span class="label">Type:</span><span class="value">${payment.payment_type === 'deposit' ? 'Deposit' : 'Full Payment'}</span></div>
              <div class="row"><span class="label">Amount:</span><span class="value">$${formatCurrencyForInvoice(paymentAmount)}</span></div>
              <div class="row"><span class="label">Date:</span><span class="value">${formatDateForInvoice(payment.created_at)}</span></div>
            </div>
          `;
          }).join('') : '<div class="payment-item">No payments recorded</div>'}
        </div>

        <div class="total">
          <div class="row">
            <span class="label">Total Paid:</span>
            <span class="value">$${formatCurrencyForInvoice(totalPaid)}</span>
          </div>
        </div>

        <div class="footer">
          <p>This is an official invoice for your records.</p>
        </div>
      </body>
      </html>
    `;
  };

  const handleDownloadInvoice = () => {
    try {
      const invoiceHTML = generateInvoiceHTML();
      const printWindow = window.open('', '_blank');
      printWindow.document.write(invoiceHTML);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    } catch (error) {
      console.error('Error generating invoice:', error);
      alert('Failed to generate invoice. Please try again later.');
    }
  };

  return { generateInvoiceHTML, handleDownloadInvoice };
};

// Default export for backward compatibility
const AdminInvoice = getAdminInvoice;
export default AdminInvoice;

